import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { encryptPassword } from '@/lib/crypto';
import { renderGoogleButton } from '@/lib/google';
import AuthLegalConsent from '@/components/auth/AuthLegalConsent';

type Role = 'learner' | 'mentor';
type RegisterStep = 'role' | 'form';

const getPasswordStrength = (pwd: string) => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (/[A-Z]/.test(pwd)) score += 1;
  if (/[0-9]/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
  return score;
};

const strengthConfig = [
  { label: '太弱', color: 'bg-red-400' },
  { label: '較弱', color: 'bg-orange-400' },
  { label: '中等', color: 'bg-yellow-400' },
  { label: '強', color: 'bg-emerald-400' },
  { label: '非常強', color: 'bg-emerald-500' }
];

const roleOptions: Array<{ value: Role; icon: string; label: string; desc: string }> = [
  { value: 'learner', icon: 'ri-graduation-cap-line', label: '求職學員', desc: '尋找導師指導' },
  { value: 'mentor', icon: 'ri-user-star-line', label: '產業導師', desc: '分享經驗帶學員' }
];

const RegisterPage = () => {
  const [step, setStep] = useState<RegisterStep>('role');
  const [roleModalOpen, setRoleModalOpen] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contactMobile, setContactMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [role, setRole] = useState<Role>('learner');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [publicKey, setPublicKey] = useState('');

  const navigate = useNavigate();
  const { completeLogin, isAuthenticated } = useAuth();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const strength = useMemo(() => getPasswordStrength(password), [password]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/account', { replace: true });
      return;
    }
    void api
      .getWebsiteInfo()
      .then((website) => setPublicKey(website.rsaLoginPublicKey || ''))
      .catch((requestError) => {
        setError(requestError instanceof Error ? requestError.message : '初始化失敗');
      });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (step !== 'role' || !googleButtonRef.current || isAuthenticated) {
      return;
    }

    let active = true;
    setGoogleLoading(true);
    void renderGoogleButton({
      container: googleButtonRef.current,
      text: 'signup_with',
      onCredential: async (googleToken) => {
        if (!active) return;
        setGoogleLoading(true);
        setError('');
        try {
          const result = await api.googleLogin({ googleToken });
          await completeLogin(result);
          navigate('/account', { replace: true });
        } catch (requestError) {
          setError(requestError instanceof Error ? requestError.message : 'Google 註冊失敗');
          setGoogleLoading(false);
        }
      },
      onError: (requestError) => {
        if (!active) return;
        setError(requestError.message);
        setGoogleLoading(false);
      }
    })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError instanceof Error ? requestError.message : 'Google 註冊初始化失敗');
      })
      .finally(() => {
        if (active) setGoogleLoading(false);
      });

    return () => {
      active = false;
    };
  }, [completeLogin, isAuthenticated, navigate, step]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void (async () => {
      if (!name.trim()) { setError('請填寫你的姓名'); return; }
      if (!email.trim()) { setError('請填寫信箱地址'); return; }
      if (role === 'mentor' && !contactMobile.trim()) { setError('導師入駐請填寫聯絡手機'); return; }
      if (password.length < 8) { setError('密碼至少需要 8 位'); return; }
      if (password !== confirmPassword) { setError('兩次密碼輸入不一致'); return; }
      if (!agreed) { setError('請先閱讀並同意服務條款、私隱政策與退款政策'); return; }
      if (!publicKey) { setError('公鑰尚未載入完成'); return; }

      setLoading(true);
      setError('');
      try {
        const mobilePwdEncrypt = await encryptPassword(password, publicKey);
        const result = await api.register({
          email: email.trim(),
          mobile: role === 'mentor' ? contactMobile.trim() : undefined,
          mobilePwdEncrypt,
          nickname: name.trim(),
          userType: role === 'mentor' ? 2 : 1
        });
        await completeLogin(result);
        setSuccess(true);
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '註冊失敗');
      } finally {
        setLoading(false);
      }
    })();
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#f8fafc_38%,_#eef2ff_100%)] px-6 py-12">
        <div className="mx-auto w-full max-w-sm rounded-3xl border border-white/70 bg-white/90 p-8 text-center shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)] backdrop-blur-xl">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
            <i className="ri-checkbox-circle-line text-emerald-500 text-4xl" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-slate-900">帳號創建成功</h2>
          <p className="mb-8 text-sm leading-relaxed text-slate-500">
            歡迎加入 NexusGo，<span className="font-semibold text-slate-700">{name}</span>
          </p>
          <Link
            to="/account"
            className="mb-3 block w-full whitespace-nowrap rounded-lg bg-violet-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
          >
            前往我的帳戶
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#f8fafc_38%,_#eef2ff_100%)] px-4 py-10 sm:px-6 sm:py-14">
      <div className="absolute left-4 top-4 sm:left-7 sm:top-6">
        <Link to="/" className="inline-flex items-center gap-3 rounded-2xl border border-white/70 bg-white/80 px-4 py-2 backdrop-blur-md shadow-sm">
          <img
            src="/assets/readdy/static-358c7e6691b9b58b5179c58a2715558b.jpg"
            alt="NexusGo"
            className="h-9 w-9 rounded-xl object-cover"
          />
          <span className="text-base font-bold tracking-tight text-[#0A1628]">NexusGo</span>
        </Link>
      </div>

      <div className="mx-auto mt-20 w-full max-w-[620px]">
        <div className="overflow-hidden rounded-[28px] border border-white/70 bg-white/90 p-6 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)] backdrop-blur-xl sm:p-8">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-black text-slate-900">建立你的帳號</h1>
            <p className="mt-1 text-sm text-slate-500">
              已有帳號？{' '}
              <Link to="/login" className="font-semibold text-violet-600 hover:text-violet-700">
                立即登入
              </Link>
            </p>
          </div>

          {error ? (
            <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
              <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                <i className="ri-information-line text-sm text-amber-500" />
              </div>
              <div className="text-xs leading-relaxed text-amber-800">{error}</div>
            </div>
          ) : null}

          {step === 'role' ? (
            <div>
              <button
                type="button"
                onClick={() => setRoleModalOpen(true)}
                className="mb-4 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 hover:border-violet-300"
              >
                選擇你的身份：<span className="font-semibold text-slate-900">{role === 'mentor' ? '產業導師' : '求職學員'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('form');
                  setError('');
                }}
                className="w-full rounded-xl bg-[#0A1628] py-3 text-sm font-semibold text-white transition-all hover:bg-[#12233f]"
              >
                繼續
              </button>

              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-100" />
                <span className="text-xs text-slate-400">使用 Google 註冊</span>
                <div className="h-px flex-1 bg-slate-100" />
              </div>

              <div
                ref={googleButtonRef}
                className={`min-h-11 transition-opacity ${googleLoading ? 'opacity-70' : 'opacity-100'}`}
              />
              {googleLoading ? (
                <div className="mt-2 flex items-center justify-center gap-2 text-xs text-slate-500">
                  <i className="ri-loader-4-line animate-spin" />
                  正在初始化 Google 註冊
                </div>
              ) : null}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <span>身份：{role === 'mentor' ? '產業導師' : '求職學員'}</span>
                <button type="button" onClick={() => setRoleModalOpen(true)} className="font-semibold text-violet-600 hover:text-violet-700">
                  變更
                </button>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">姓名</label>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="你的姓名"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">信箱地址</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="your@email.com"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                />
              </div>

              {role === 'mentor' ? (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">聯絡手機</label>
                  <input
                    type="text"
                    value={contactMobile}
                    onChange={(event) => setContactMobile(event.target.value)}
                    placeholder="導師聯絡手機"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                  />
                </div>
              ) : null}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">設定密碼</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="至少 8 位"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-slate-400 hover:text-slate-600"
                  >
                    <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                  </button>
                </div>
                {password ? (
                  <div className="mt-2">
                    <div className="mb-1 flex gap-1">
                      {[1, 2, 3, 4].map((index) => (
                        <div
                          key={index}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${index <= strength + 1 ? strengthConfig[strength].color : 'bg-slate-100'}`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs ${strength <= 1 ? 'text-red-500' : strength <= 2 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                      密碼強度：{strengthConfig[strength]?.label}
                    </p>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">確認密碼</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="再次輸入密碼"
                    className={`w-full rounded-xl border px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition-all focus:outline-none focus:ring-2 ${
                      confirmPassword && password !== confirmPassword
                        ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                        : confirmPassword && password === confirmPassword
                          ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-100'
                          : 'border-slate-200 focus:border-violet-400 focus:ring-violet-100'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((value) => !value)}
                    className="absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-slate-400 hover:text-slate-600"
                  >
                    <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                  </button>
                </div>
              </div>

              <AuthLegalConsent checked={agreed} onChange={setAgreed} compact />

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-violet-600 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    註冊中...
                  </>
                ) : (
                  '免費建立帳號'
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {roleModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/35 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border border-white/70 bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0A1628]">選擇你的身份</h3>
              <button type="button" onClick={() => setRoleModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="space-y-2.5">
              {roleOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRole(option.value)}
                  className={`w-full rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    role === option.value ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <i className={`${option.icon} text-base ${role === option.value ? 'text-violet-600' : 'text-slate-500'}`} />
                    <div>
                      <p className={`text-sm font-semibold ${role === option.value ? 'text-violet-700' : 'text-slate-700'}`}>{option.label}</p>
                      <p className="text-xs text-slate-400">{option.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setRoleModalOpen(false);
                if (step === 'role') {
                  setStep('form');
                }
              }}
              className="mt-4 w-full rounded-xl bg-[#0A1628] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#12233f]"
            >
              確認並繼續
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RegisterPage;
